<template>
  <v-navigation-drawer
    v-model="drawer"
    temporary
    right
    width="450"
    height="100%"
    app
  >
    <v-card
      v-if="promo_code"
      flat
    >
      <v-card-title>
        {{ title }}
      </v-card-title>
      <v-card-subtitle class="caption">
        <v-row>
          <v-col
            v-if="promo_code.id"
            cols="auto"
          >
            <span class="font-weight-bold">Id</span> {{ promo_code.id }}
          </v-col>
        </v-row>
      </v-card-subtitle>
      <v-divider />
      <v-card-text>
        <v-form
          ref="form"
          v-model="valid"
        >
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="promo_code.code"
                label="Code"
                outlined
                hide-details="auto"
                :disabled="!isAdd"
                :rules="[$rules.required]"
              />
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="promo_code.productTypeId"
                label="Type"
                :items="product_types"
                :rules="[$rules.required]"
                outlined
                hide-details="auto"
                item-value="id"
                item-text="name"
                :disabled="!isAdd"
              />
            </v-col>
            <v-col cols="12">
              <v-menu
                v-model="start_menu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="promo_code.startDate"
                    label="Start"
                    readonly
                    v-bind="attrs"
                    outlined
                    hide-details="auto"
                    clearable
                    :rules="[$rules.required]"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="promo_code.startDate"
                  :max="promo_code.endDate"
                  @input="start_menu = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="12">
              <v-menu
                v-model="end_menu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="promo_code.endDate"
                    label="End"
                    readonly
                    v-bind="attrs"
                    outlined
                    hint="Valid through the end of this day (expires at 11:59:59 PM)."
                    persistent-hint
                    :rules="[$rules.required, endGreater]"
                    clearable
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="promo_code.endDate"
                  :min="promo_code.startDate"
                  @input="end_menu = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="promo_code.value"
                label="Value"
                :rules="[$rules.required]"
                outlined
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12">
              <v-switch
                v-model="promo_code.isPercentageDiscount"
                label="Percentage?"
                color="primary"
                inset
                hide-details
                outlined
                class="pt-0 mt-0"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="promo_code.minimumProductValue"
                label="Minimum Product Value"
                outlined
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="promo_code.usesRemaining"
                label="Uses Remaining"
                hint="Total redemptions allowed across all customers. Counts down by one per order and stops the code at zero."
                persistent-hint
                outlined
              />
            </v-col>
            <v-col cols="12">
              <v-switch
                v-model="promo_code.multipleUse"
                label="Multiple Use"
                hint="On: the same customer can redeem this code more than once. Off: each customer can use it only once."
                persistent-hint
                color="primary"
                inset
                outlined
                class="pt-0 mt-0"
              />
            </v-col>
            <v-col cols="12">
              <v-switch
                v-model="promo_code.firstPrintOnly"
                label="First Print Order Only"
                hint="Only redeemable by a customer who has never placed a print order"
                persistent-hint
                color="primary"
                inset
                outlined
                class="pt-0 mt-0"
              />
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                v-model="promo_code.masterAccountId"
                label="Limit to Master Account"
                :items="master_accounts"
                hint="Leave empty for a global code anyone can redeem. Otherwise only that master account's customers can use it."
                persistent-hint
                clearable
                outlined
                item-value="id"
                item-text="name"
              />
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                v-model="promo_code.categoryIds"
                label="Limit to Categories"
                :items="categories"
                :loading="loading_categories"
                hint="Leave empty to allow every category. Otherwise the discount only applies to cart items in these categories."
                persistent-hint
                multiple
                chips
                deletable-chips
                small-chips
                clearable
                outlined
                item-value="id"
                item-text="name"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
    <template #append>
      <v-divider />
      <v-card-text>
        <v-btn
          color="primary"
          :loading="saving"
          block
          @click="save"
        >
          Save
        </v-btn>
        <v-btn
          v-if="!isAdd"
          color="error"
          class="mt-2"
          :loading="deleting"
          text
          block
          @click="remove"
        >
          Delete
        </v-btn>
      </v-card-text>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from 'vuex';
export default {
  name: 'EditPromoCode',

  components: {},

  data() {
    return {
      loading: false,
      loading_categories: false,
      deleting: false,
      valid: false,
      saving: false,
      start_menu: false,
      end_menu: false,
      drawer: false
    };
  },

  computed: {
    isAdd() {
      return !this.promo_code.id;
    },
    title() {
      return !this.isAdd ? 'Edit Promo Code' : 'Add Promo Code';
    },
    promo_code: {
      get() {
        return this.$store.getters['promocodes/edit_promo_code'];
      },
      set(val) {
        this.$store.dispatch('promocodes/edit', val);
      }
    },
    ...mapGetters({
      promo_codes: 'promocodes/list',
      master_accounts: 'lookup/master_accounts',
      product_types: 'lookup/product_types',
      categories: 'lookup/categories'
    })
  },

  async mounted() {
    this.loadCategories();
    this.$bus.$off(this.$enums.EVENT_BUS_EVENTS.PROMO_CODE_EDIT_SHOW);
    this.$bus.$on(this.$enums.EVENT_BUS_EVENTS.PROMO_CODE_EDIT_SHOW, (data) => {
      const item = this.promo_codes.find((x) => x.id === data.id) || {};
      // the multi-select needs an array even when adding a code
      this.$store.dispatch('promocodes/set', { ...item, categoryIds: item.categoryIds || [] });
      this.drawer = true;
    });
  },

  methods: {
    loadCategories() {
      if (this.categories.length) return;
      this.loading_categories = true;
      this.$store
        .dispatch('lookup/getCategories')
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.loading_categories = false;
        });
    },
    async remove() {
      if (!confirm(`Delete ${this.promo_code.code}? It will stop working immediately.`)) return;
      this.deleting = true;
      this.$store
        .dispatch('promocodes/remove', this.promo_code.id)
        .then(() => {
          this.$root.$snackbarSuccess('Promo Code deleted');
          this.drawer = false;
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      this.$store
        .dispatch('promocodes/save', this.promo_code)
        .then(() => {
          this.$root.$snackbarSuccess('Promo Code saved');
          this.drawer = false;
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    endGreater(val) {
      return (
        !(this.promo_code.startDate && val && this.promo_code.startDate > val) ||
        'End date must be after Start Date'
      );
    }
  }
};
</script>
